
// @ts-nocheck
import { supabase } from './supabase';
import { Database } from '../types/database';

type Review = Database['public']['Tables']['reviews']['Row'];
type ReviewInsert = Database['public']['Tables']['reviews']['Insert'];
type ReviewUpdate = Database['public']['Tables']['reviews']['Update'];

export interface ReviewInput {
  business_id: string;
  customer_id: string;
  booking_id?: string;
  rating: number;
  comment?: string;
}

export interface ReviewServiceResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

export const submitReview = async (reviewData: ReviewInput): Promise<ReviewServiceResponse<Review>> => {
  try {
    const insertData: ReviewInsert = {
      business_id: reviewData.business_id,
      customer_id: reviewData.customer_id,
      booking_id: reviewData.booking_id || null,
      rating: reviewData.rating,
      comment: reviewData.comment || null,
      verified: false,
      moderated: false
    };

    const { data, error } = await supabase
      .from('reviews')
      .insert([insertData])
      .select()
      .single();

    if (error) {
      return {
        success: false,
        error: {
          code: 'REVIEW_SUBMIT_FAILED',
          message: error.message,
          details: error
        }
      };
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'REVIEW_SUBMIT_FAILED',
        message: 'Failed to submit review',
        details: error
      }
    };
  }
};

export const updateReview = async (reviewId: string, updates: Partial<ReviewUpdate>): Promise<ReviewServiceResponse<Review>> => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .update(updates)
      .eq('id', reviewId)
      .select()
      .single();

    if (error) {
      return {
        success: false,
        error: {
          code: 'REVIEW_UPDATE_FAILED',
          message: error.message,
          details: error
        }
      };
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'REVIEW_UPDATE_FAILED',
        message: 'Failed to update review',
        details: error
      }
    };
  }
};

export const deleteReview = async (reviewId: string): Promise<ReviewServiceResponse<boolean>> => {
  try {
    const { error } = await supabase
      .from('reviews')
      .delete()
      .eq('id', reviewId);

    if (error) {
      return {
        success: false,
        error: {
          code: 'REVIEW_DELETE_FAILED',
          message: error.message,
          details: error
        }
      };
    }

    return {
      success: true,
      data: true
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'REVIEW_DELETE_FAILED',
        message: 'Failed to delete review',
        details: error
      }
    };
  }
};

export const moderateReview = async (
  reviewId: string,
  moderatorId: string,
  action: 'approve' | 'reject',
  reason?: string
): Promise<ReviewServiceResponse<Review>> => {
  try {
    const updates: ReviewUpdate = {
      moderated: true,
      moderator_id: moderatorId,
      moderation_reason: reason || null
    };

    // If rejecting, we might want to mark as moderated but not change verified status
    // If approving, set verified to true
    if (action === 'approve') {
      updates.verified = true;
    }

    const { data, error } = await supabase
      .from('reviews')
      .update(updates)
      .eq('id', reviewId)
      .select()
      .single();

    if (error) {
      return {
        success: false,
        error: {
          code: 'REVIEW_MODERATION_FAILED',
          message: error.message,
          details: error
        }
      };
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'REVIEW_MODERATION_FAILED',
        message: 'Failed to moderate review',
        details: error
      }
    };
  }
};

export const getReviewsForBusiness = async (
  businessId: string,
  options?: {
    limit?: number;
    offset?: number;
    verifiedOnly?: boolean;
  }
): Promise<ReviewServiceResponse<Review[]>> => {
  try {
    let query = supabase
      .from('reviews')
      .select(`
        *,
        user_profiles!reviews_customer_id_fkey (
          full_name,
          email
        )
      `)
      .eq('business_id', businessId)
      
      .order('created_at', { ascending: false });

    if (options?.verifiedOnly) {
      query = query.eq('verified', true);
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    if (options?.offset) {
      query = query.range(options.offset, (options.offset + (options.limit || 10)) - 1);
    }

    const { data, error } = await query;

    if (error) {
      return {
        success: false,
        error: {
          code: 'REVIEWS_FETCH_FAILED',
          message: error.message,
          details: error
        }
      };
    }

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'REVIEWS_FETCH_FAILED',
        message: 'Failed to fetch reviews',
        details: error
      }
    };
  }
};

export const getBusinessRatingStats = async (businessId: string): Promise<ReviewServiceResponse<{
  averageRating: number;
  totalReviews: number;
  ratingDistribution: { [key: number]: number };
}>> => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select('rating')
      .eq('business_id', businessId)
      .eq('verified', true)
      .eq('moderated', true)
      ;

    if (error) {
      return {
        success: false,
        error: {
          code: 'RATING_STATS_FAILED',
          message: error.message,
          details: error
        }
      };
    }

    const reviews = data || [];
    const totalReviews = reviews.length;

    if (totalReviews === 0) {
      return {
        success: true,
        data: {
          averageRating: 0,
          totalReviews: 0,
          ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
        }
      };
    }

    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    const averageRating = Math.round((sum / totalReviews) * 10) / 10;

    const ratingDistribution = reviews.reduce((acc, review) => {
      acc[review.rating] = (acc[review.rating] || 0) + 1;
      return acc;
    }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } as { [key: number]: number });

    return {
      success: true,
      data: {
        averageRating,
        totalReviews,
        ratingDistribution
      }
    };
  } catch (error) {
   return {
     success: false,
     error: {
       code: 'RATING_STATS_FAILED',
       message: 'Failed to calculate rating statistics',
       details: error
     }
   };
 }
};

export const getAllReviews = async (options?: {
  limit?: number;
  offset?: number;
  status?: 'pending' | 'approved' | 'rejected' | 'all';
}): Promise<ReviewServiceResponse<Review[]>> => {
  try {
    let query = supabase
      .from('reviews')
      .select(`
        *,
        user_profiles!reviews_customer_id_fkey (
          full_name,
          email
        ),
        businesses!reviews_business_id_fkey (
          name
        )
      `)
      .order('created_at', { ascending: false });

    if (options?.status) {
      switch (options.status) {
        case 'pending':
          query = query.eq('moderated', false);
          break;
        case 'approved':
          query = query.eq('moderated', true).eq('verified', true);
          break;
        case 'rejected':
          query = query.eq('moderated', true).eq('verified', false);
          break;
        case 'all':
        default:
          // No filter
          break;
      }
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    if (options?.offset) {
      query = query.range(options.offset, (options.offset + (options.limit || 10)) - 1);
    }

    const { data, error } = await query;

    if (error) {
      return {
        success: false,
        error: {
          code: 'REVIEWS_FETCH_FAILED',
          message: error.message,
          details: error
        }
      };
    }

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    return {
      success: false,
      error: {
        code: 'REVIEWS_FETCH_FAILED',
        message: 'Failed to fetch reviews',
        details: error
      }
    };
  }
};